'use client';

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/platform/lib/api';
import { useAuth } from '@/platform/auth/useAuth';

export type MarketplaceCommunityStatus = {
  ready: boolean;
  enabled: boolean;
  canManage: boolean;
  slug: string;
  webhooksReady: boolean;
};

const EMPTY: MarketplaceCommunityStatus = {
  ready: false,
  enabled: false,
  canManage: false,
  slug: '',
  webhooksReady: false,
};

export type MarketplaceCommunityStatusHook = {
  status: MarketplaceCommunityStatus;
  reload: () => Promise<void>;
};

export function useMarketplaceCommunityStatus(enabled = true): MarketplaceCommunityStatusHook {
  const { user, isLoading } = useAuth();
  const [status, setStatus] = useState<MarketplaceCommunityStatus>(EMPTY);

  const reload = useCallback(async () => {
    if (!enabled || isLoading) return;
    if (!user) {
      setStatus({ ready: true, enabled: false, canManage: false, slug: '', webhooksReady: false });
      return;
    }
    setStatus((prev) => ({ ...prev, ready: false }));
    try {
      const st = await apiFetch<{
        enabled?: boolean;
        canManage?: boolean;
        slug?: string;
        webhooksReady?: boolean;
      }>('/saas/me/marketplace/status');
      setStatus({
        ready: true,
        enabled: !!st.enabled,
        canManage: !!st.canManage,
        slug: st.slug ?? '',
        webhooksReady: !!st.webhooksReady,
      });
    } catch {
      setStatus({ ready: true, enabled: false, canManage: false, slug: '', webhooksReady: false });
    }
  }, [enabled, isLoading, user]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { status, reload };
}
